import { useEffect } from "react";

/**
 * Per-route SEO updater. Updates <title>, description, canonical, OG tags
 * without needing react-helmet-async. Crawlers like Googlebot render JS so this works.
 */
export function useSEO({ title, description, path, ogImage, keywords, noindex }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [_, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
        const isProperty = selector.includes("property=");
        if (name) el.setAttribute(isProperty ? "property" : "name", name);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Robots: indexable per-page by default; only genuine 404 / soft-404 routes opt out
    // via noindex:true. (No host-based noindex — in a pre-rendered/CDN setup that can bake
    // noindex into production snapshots.)
    setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    setMeta('meta[name="googlebot"]', "content", noindex ? "noindex, nofollow" : "index, follow");

    // Remove the default static canonical so we always set the correct per-page one below
    const staticCanonical = document.querySelector('link[rel="canonical"][data-default-canonical]');
    if (staticCanonical) staticCanonical.remove();

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (keywords && keywords.length) {
      const val = Array.isArray(keywords) ? keywords.join(", ") : String(keywords);
      setMeta('meta[name="keywords"]', "content", val);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', "content", ogImage);
      setMeta('meta[name="twitter:image"]', "content", ogImage);
    }
    if (path) {
      const url = `https://betterthroughboxing.com${path}`;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = url;
      setMeta('meta[property="og:url"]', "content", url);
    }
  }, [title, description, path, ogImage, keywords, noindex]);
}
